import { useTranslation } from '../../i18n/hooks/useTranslation';
import { CARD } from './contestUi';

// Loading placeholder for the /contest page. It repeats the real page block by block — hero → my standing →
// (ways to earn + bonus tasks + invite | leaderboard) — with the same grids, paddings and column widths, so
// nothing jumps when the data arrives. Shown for the first load only (ContestPage swaps it for the real blocks).

/** One grey bar. `dark` = on the always-dark hero. `round` is the whole radius class (one value, so nothing fights). */
function Bar({ className = 'h-4 w-24', dark = false, round = 'rounded-md' }) {
    return <div className={`${round} ${dark ? 'bg-white/10' : 'bg-zinc-200 dark:bg-zinc-700/70'} ${className}`} />;
}

// Podium: 2nd · 1st · 3rd — same avatar and block heights as ContestTopList.
const PODIUM = [
    { avatar: 'h-[52px] w-[52px]', block: 'h-[70px]' },
    { avatar: 'h-16 w-16', block: 'h-[92px]' },
    { avatar: 'h-[52px] w-[52px]', block: 'h-[58px]' },
];

export default function ContestSkeleton() {
    const { t } = useTranslation();

    return (
        <div role="status" aria-busy="true" className="flex flex-col gap-5 animate-pulse motion-reduce:animate-none">
            <span className="sr-only">{t('common.loading')}</span>

            {/* ── Hero (ContestHero) ── */}
            <div className="relative flex flex-wrap items-start justify-between gap-7 overflow-hidden rounded-[22px] bg-zinc-900 p-5 sm:p-7 dark:bg-zinc-950 dark:ring-1 dark:ring-zinc-800">
                <div className="flex min-w-0 flex-[1_1_380px] flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-2.5">
                        <Bar dark round="rounded-full" className="h-[26px] w-28" />
                        <Bar dark className="h-4 w-44" />
                    </div>
                    {/* The slim bars sit in boxes as tall as the real text lines (two lines on a phone, one from sm up), so the
                        hero is as tall as the real one. */}
                    <div className="flex h-[67px] flex-col justify-center gap-2 sm:h-10">
                        <Bar dark className="h-7 w-full max-w-[460px] sm:h-9 sm:w-3/4" />
                        <Bar dark className="h-7 w-2/3 sm:hidden" />
                    </div>
                    <div className="flex h-[45px] flex-col justify-around sm:h-[22px]">
                        <Bar dark className="h-4 w-full max-w-[560px]" />
                        <Bar dark className="h-4 w-3/5 sm:hidden" />
                    </div>
                    <div className="mt-1.5 flex max-w-[560px] flex-col gap-[7px]">
                        <div className="flex h-[19px] items-center justify-between gap-3">
                            <Bar dark className="h-3.5 w-16" />
                            <Bar dark className="h-3.5 w-28" />
                        </div>
                        <Bar dark round="rounded-full" className="h-1.5 w-full" />
                    </div>
                </div>
                <div className="flex max-w-full flex-col items-start gap-4 lg:items-end">
                    <Bar dark round="rounded-full" className="h-[38px] w-64 max-w-full" />
                    <div className="flex flex-col gap-2.5">
                        <div className="flex h-5 items-center"><Bar dark className="h-4 w-36" /></div>
                        <div className="grid grid-cols-[repeat(4,minmax(64px,82px))] gap-2.5">
                            {[0, 1, 2, 3].map((i) => <Bar key={i} dark round="rounded-[14px]" className="h-[73px]" />)}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── My standing (ContestStandingCard) ── */}
            <div className={`${CARD} grid grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] items-center gap-x-7 gap-y-5 px-5 py-5 sm:px-6 sm:py-[22px]`}>
                <div className="flex items-center gap-4">
                    <Bar round="rounded-[18px]" className="h-[68px] w-[68px] flex-none" />
                    <div className="flex min-w-0 flex-col gap-2">
                        <Bar className="h-3.5 w-24" />
                        <Bar className="h-8 w-32" />
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <div className="flex h-[22px] items-center"><Bar className="h-4 w-3/4" /></div>
                    <Bar round="rounded-full" className="h-2 w-full" />
                    <div className="flex h-[19px] items-center"><Bar className="h-3 w-2/3" /></div>
                </div>
                <div className="flex flex-col gap-2.5">
                    <Bar round="rounded-full" className="h-2 w-full" />
                    <div className="grid grid-cols-2 gap-x-3.5 gap-y-1">
                        {[0, 1, 2, 3].map((i) => <div key={i} className="flex h-[19px] items-center"><Bar className="h-3 w-full" /></div>)}
                    </div>
                </div>
            </div>

            {/* ── Same two-column grid as the page ── */}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,480px),1fr))] items-start gap-5">
                {/* LEFT — ways to earn (ContestEarnCard) */}
                <div className="flex min-w-0 flex-col gap-5">
                    <div className="flex flex-col gap-3">
                        <Bar className="h-6 w-48" />
                        <div className="grid grid-cols-2 gap-2.5">
                            {[0, 1, 2, 3].map((i) => (
                                <div key={i} className="flex flex-col gap-2 rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
                                    <Bar className="h-7 w-16" />
                                    <Bar className="h-4 w-3/4" />
                                    <Bar className="h-3 w-full" />
                                    <Bar className="h-3 w-2/3" />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Bonus tasks (ContestBonusTasks) */}
                    <div className={`${CARD} flex flex-col gap-3.5 p-[22px]`}>
                        <div className="flex items-center justify-between gap-3">
                            <Bar className="h-6 w-28" />
                            <Bar className="h-4 w-40" />
                        </div>
                        <div className="flex flex-col gap-2">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <div key={i} className="flex items-center gap-3.5 rounded-[14px] border border-zinc-100 py-3 pl-3.5 pr-3 dark:border-zinc-800">
                                    <Bar round="rounded-[11px]" className="h-10 w-10 flex-none" />
                                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                                        <Bar className="h-4 w-3/4" />
                                        <Bar round="rounded-full" className="h-4 w-20" />
                                    </div>
                                    <div className="flex flex-none items-center gap-2.5">
                                        <Bar className="h-4 w-8" />
                                        <Bar round="rounded-[10px]" className="h-[38px] w-[104px] max-sm:w-20" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Invite a friend (ContestReferralCard) */}
                    <div className={`${CARD} flex flex-col gap-4 p-[22px]`}>
                        <div className="flex flex-col gap-2">
                            <Bar className="h-5 w-44" />
                            <Bar className="h-4 w-3/4" />
                        </div>
                        <Bar round="rounded-[12px]" className="h-12 w-full" />
                        <div className="grid grid-cols-2 gap-2 border-t border-zinc-100 pt-3.5 dark:border-zinc-800">
                            {[0, 1].map((i) => (
                                <div key={i} className="flex flex-col gap-2">
                                    <Bar className="h-6 w-20" />
                                    <Bar className="h-3.5 w-28" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* RIGHT — leaderboard (ContestTopList) */}
                <div className={`${CARD} flex min-w-0 flex-col overflow-hidden`}>
                    <div className="flex flex-col gap-3.5 px-[22px] pb-3.5 pt-[22px]">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="flex flex-col gap-2">
                                <Bar className="h-5 w-32" />
                                <Bar className="h-3.5 w-56 max-w-full" />
                            </div>
                            <Bar round="rounded-[11px]" className="h-10 w-40" />
                        </div>
                        <div className="grid grid-cols-[1fr_1.1fr_1fr] items-end gap-2 pt-1.5">
                            {PODIUM.map((p, i) => (
                                <div key={i} className="flex min-w-0 flex-col items-center gap-1.5">
                                    <Bar round="rounded-full" className={p.avatar} />
                                    <Bar className="mt-1 h-4 w-20 max-w-full" />
                                    <Bar round="rounded-b-[4px] rounded-t-xl" className={`w-full ${p.block}`} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="border-y border-zinc-100 bg-zinc-50 px-[22px] py-2 dark:border-zinc-800 dark:bg-zinc-800/40">
                        <Bar className="h-3 w-full" />
                    </div>
                    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                        <div key={i} className="grid grid-cols-[40px_1fr_auto] items-center gap-3 border-b border-zinc-100 px-[22px] py-2.5 dark:border-zinc-800">
                            <Bar className="h-4 w-5" />
                            <div className="flex min-h-[42px] min-w-0 items-center gap-[11px]">
                                <Bar round="rounded-full" className="h-9 w-9 flex-none" />
                                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                    <Bar className="h-4 w-2/3" />
                                    <Bar className="h-3 w-1/2" />
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-1.5">
                                <Bar className="h-4 w-10" />
                                <Bar className="h-3 w-6" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
